import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { Phone, MessageCircle, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import CarModel from './CarModel';

// Crack SVG animation component
const CrackAnimation = () => (
  <svg className="crack-svg" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice">
    <defs>
      <filter id="glow">
        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    <g filter="url(#glow)">
      <path className="crack-path" d="M200,0 L190,80 L210,120 L180,200 L220,280 L200,400" />
      <path className="crack-path" d="M200,200 L120,180 L80,220 L0,200" style={{ animationDelay: '0.5s' }} />
      <path className="crack-path" d="M200,200 L280,180 L320,220 L400,200" style={{ animationDelay: '1s' }} />
      <path className="crack-path" d="M200,200 L160,120 L140,80" style={{ animationDelay: '1.5s' }} />
      <path className="crack-path" d="M200,200 L240,280 L260,320" style={{ animationDelay: '2s' }} />
    </g>
  </svg>
);

export default function HeroSection() {
  const heroRef = useRef(null);
  const textRef = useRef(null);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [showCrack, setShowCrack] = useState(true);

  useEffect(() => {
    // GSAP animations for text entrance
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.hero-title',
        { opacity: 0, y: 100, scale: 0.9 },
        { opacity: 1, y: 0, scale: 1, duration: 1.2, ease: 'power4.out', delay: 0.3 }
      );
      
      gsap.fromTo(
        '.hero-subtitle',
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 1, ease: 'power3.out', delay: 0.6 }
      );
      
      gsap.fromTo(
        '.hero-buttons',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out', delay: 0.9 }
      );
      
      gsap.fromTo(
        '.hero-badge',
        { opacity: 0, scale: 0.8 },
        { opacity: 1, scale: 1, duration: 0.6, ease: 'back.out(1.7)', delay: 1.2 }
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  // Cursor glow effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setCursorPos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Toggle crack animation periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setShowCrack(prev => !prev);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const scrollToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section 
      ref={heroRef} 
      className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background elements */}
      <div className="hero-bg noise-overlay"></div>
      <div className="hero-grid"></div>
      
      {/* Cursor glow */}
      <div 
        className="cursor-glow hidden lg:block"
        style={{ left: cursorPos.x, top: cursorPos.y }}
      />
      
      {/* Crack animation overlay */}
      {showCrack && (
        <div className="absolute inset-0 z-10 pointer-events-none opacity-40">
          <CrackAnimation />
        </div>
      )}
      
      {/* Main content */}
      <div className="relative z-20 container mx-auto px-4 py-20">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Text content */}
          <div ref={textRef} className="text-center lg:text-left order-2 lg:order-1">
            {/* Badges */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-3 mb-6">
              <div className="hero-badge inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full px-4 py-2">
                <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse"></span>
                <span className="text-cyan-400 text-sm font-medium tracking-wide">OPEN 24/7</span>
              </div>
              <div className="hero-badge inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/30 rounded-full px-4 py-2">
                <span className="text-blue-400 text-sm font-medium tracking-wide">AIS Authorized Dealer</span>
              </div>
            </div>
            
            {/* Main title */}
            <h1 className="hero-title font-orbitron text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black uppercase tracking-wider mb-6">
              <span className="block text-white">Doon</span>
              <span className="block gradient-text neon-text">Auto Glass</span>
            </h1>
            
            {/* Subtitle */}
            <p className="hero-subtitle text-lg sm:text-xl lg:text-2xl text-gray-300 mb-8 max-w-xl mx-auto lg:mx-0">
              Fast, Reliable & <span className="text-cyan-400">24/7</span> Car Glass Repair in Dehradun
            </p>
            
            {/* Stats badges */}
            <div className="hero-buttons flex flex-wrap justify-center lg:justify-start gap-4 mb-8">
              <div className="glass-card px-4 py-2 rounded-lg">
                <span className="text-cyan-400 font-orbitron font-bold">4+</span>
                <span className="text-gray-400 text-sm ml-2">Years Exp</span>
              </div>
              <div className="glass-card px-4 py-2 rounded-lg">
                <span className="text-cyan-400 font-orbitron font-bold">5.0</span>
                <span className="text-yellow-400 ml-1">★</span>
                <span className="text-gray-400 text-sm ml-2">Rating</span>
              </div>
              <div className="glass-card px-4 py-2 rounded-lg">
                <span className="text-cyan-400 font-orbitron font-bold">1000+</span>
                <span className="text-gray-400 text-sm ml-2">Cars Fixed</span>
              </div>
            </div>
            
            {/* CTA Buttons */}
            <div className="hero-buttons flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
              <a href="tel:+917300677651" data-testid="hero-call-btn">
                <Button 
                  className="skew-btn bg-cyan-500 hover:bg-cyan-400 text-black font-bold uppercase tracking-widest px-8 py-6 text-lg transition-all hover:shadow-[0_0_30px_rgba(0,212,255,0.6)]"
                >
                  <span className="flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Call Now
                  </span>
                </Button>
              </a>
              
              <a href="https://wa.me/917300677651" target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn">
                <Button 
                  variant="outline"
                  className="skew-btn border-2 border-green-500 text-green-400 hover:bg-green-500/10 font-bold uppercase tracking-widest px-8 py-6 text-lg"
                >
                  <span className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5" />
                    WhatsApp
                  </span>
                </Button>
              </a>
              
              <Button 
                variant="outline"
                className="skew-btn border-2 border-white/30 text-white hover:bg-white/10 font-bold uppercase tracking-widest px-8 py-6 text-lg"
                onClick={scrollToServices}
                data-testid="hero-quote-btn"
              >
                <span>Get Free Quote</span>
              </Button>
            </div>
          </div>
          
          {/* 3D Car Model */}
          <div className="order-1 lg:order-2 relative">
            <CarModel />
            
            {/* Glass shimmer overlay */}
            <div className="absolute inset-0 pointer-events-none glass-shimmer rounded-3xl"></div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <button 
            onClick={scrollToServices}
            className="text-gray-400 hover:text-cyan-400 transition-colors"
            aria-label="Scroll to services"
            data-testid="scroll-indicator"
          >
            <ChevronDown className="w-8 h-8" />
          </button>
        </div>
      </div>
    </section>
  );
}
